import { createBrowserRouter, RouterProvider, Navigate, Outlet, useLocation } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from './components/ui/sonner'
import { TooltipProvider } from './components/ui/tooltip'
import { AuthService } from '@/services/core/auth'
import { ThemeProvider } from './contexts/ThemeContext'
import { UserProvider } from './contexts/UserContext'
import { ModuleProvider } from './contexts/ModuleContext'
import { NetworkProvider } from './contexts/NetworkContext'
import { NetworkProfileProvider } from './contexts/NetworkProfileContext'
import { UserProfileProvider } from './contexts/UserProfileContext'
import { NotificationsProvider } from './contexts/NotificationsContext'
import { CommandPalette } from './components/command-palette'

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
})
import { LoginPage } from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { ElectionsPage } from './pages/political/ElectionsPage'
import { ElectionDetailPage } from './pages/political/ElectionDetailPage'
import { PoliticalEntitiesPage } from './pages/political/PoliticalEntitiesPage'
import { PoliticalEntityEditPage } from './pages/political/PoliticalEntityEditPage'
import { PoliticalCampaignsPage } from './pages/political/PoliticalCampaignsPage'
import { PoliticalCampaignEditPage } from './pages/political/PoliticalCampaignEditPage'
import { PublicCampaignInfoPage } from './pages/political/PublicCampaignInfoPage'
import { PoliticalPreviewPage } from './pages/political/PoliticalPreviewPage'
import { CreativeBlankPreviewPage } from './pages/creatives/CreativeBlankPreviewPage'
import { CreativePreviewRenderPage } from './pages/creatives/CreativePreviewRenderPage'
import { AiConnectPage } from './pages/AiConnectPage'
import { AdvertisersPage } from './pages/advertisers/AdvertisersPage'
import { AdvertiserEditPage } from './pages/advertisers/AdvertiserEditPage'
import { OrdersPage } from './pages/orders/OrdersPage'
import { OrderEditPage } from './pages/orders/OrderEditPage'
import { LineItemsPage } from './pages/line-items/LineItemsPage'
import { LineItemEditPage } from './pages/line-items/LineItemEditPage'
import { CreativesPage } from './pages/creatives/CreativesPage'
import { CreativeEditPage } from './pages/creatives/CreativeEditPage'
import { CreativeBulkUploadPage } from './pages/creatives/CreativeBulkUploadPage'
import { EarningsAccountsPage } from './pages/earnings-accounts/EarningsAccountsPage'
import { EarningsAccountEditPage } from './pages/earnings-accounts/EarningsAccountEditPage'
import { SitesPage } from './pages/sites/SitesPage'
import { SiteEditPage } from './pages/sites/SiteEditPage'
import { AdUnitsPage } from './pages/ad-units/AdUnitsPage'
import { AdUnitEditPage } from './pages/ad-units/AdUnitEditPage'
import { BlocklistsPage } from './pages/blocklists/BlocklistsPage'
import { SiteGroupsPage } from './pages/site-groups/SiteGroupsPage'
import { SiteGroupEditPage } from './pages/site-groups/SiteGroupEditPage'
import { BlocklistEditPage } from './pages/blocklists/BlocklistEditPage'
import { RulesetsPage } from './pages/rulesets/RulesetsPage'
import { RulesetEditPage } from './pages/rulesets/RulesetEditPage'
import { LayoutsPage } from './pages/layouts/LayoutsPage'
import { LayoutEditPage } from './pages/layouts/LayoutEditPage'
import { ErrorPage } from './pages/ErrorPage'
import { UsersPage } from './pages/users/UsersPage'
import { UserEditPage } from './pages/users/UserEditPage'
import { PublishingHomePage } from './pages/publishing/PublishingHomePage'
import { AdvertisingHomePage } from './pages/advertising/AdvertisingHomePage'
import { QuickReportPage } from './pages/reports/QuickReportPage'
import { ReportsPage } from './pages/reports/ReportsPage'
import { ReportTemplatesPage } from './pages/reports/ReportTemplatesPage'
import { ReportTemplateEditPage } from './pages/reports/ReportTemplateEditPage'
import { ReportSchedulesPage } from './pages/reports/ReportSchedulesPage'
import { ReportScheduleEditPage } from './pages/reports/ReportScheduleEditPage'
import { ReportTranslationsPage } from './pages/reports/ReportTranslationsPage'
import { ReportTranslationEditPage } from './pages/reports/ReportTranslationEditPage'
import { ExplorePage } from './pages/explore/ExplorePage'
import { NetworkEditPage } from './pages/admin/NetworkEditPage'
import { TeamsPage } from './pages/admin/TeamsPage'
import { ProductsPage } from './pages/admin/ProductsPage'
import { ProductEditPage } from './pages/admin/ProductEditPage'
import { TeamEditPage } from './pages/admin/TeamEditPage'
import { TeamGroupsPage } from './pages/admin/TeamGroupsPage'
import { AdminUserEditPage } from './pages/admin/AdminUserEditPage'
import { TeamGroupEditPage } from './pages/admin/TeamGroupEditPage'
import { RolesPage } from './pages/admin/RolesPage'
import { RoleEditPage } from './pages/admin/RoleEditPage'
import { CustomEventsPage } from './pages/admin/CustomEventsPage'
import { CustomEventEditPage } from './pages/admin/CustomEventEditPage'
import { TiersPage } from './pages/admin/TiersPage'
import { TierEditPage } from './pages/admin/TierEditPage'
import { DataExportsPage } from './pages/admin/DataExportsPage'
import { DataExportEditPage } from './pages/admin/DataExportEditPage'
import { EmailTemplatesPage } from './pages/admin/EmailTemplatesPage'
import { EmailTemplateEditPage } from './pages/admin/EmailTemplateEditPage'
import { EmailTranslationsPage } from './pages/admin/EmailTranslationsPage'
import { EmailTranslationEditPage } from './pages/admin/EmailTranslationEditPage'
import { NotificationPreferencesPage } from './pages/admin/NotificationPreferencesPage'
import { ReleaseNotesPage } from './pages/super-admin/ReleaseNotesPage'
import { ReleaseNoteEditPage } from './pages/super-admin/ReleaseNoteEditPage'
import { DiagnosticsPage } from './pages/super-admin/DiagnosticsPage'
import { ModulePlaceholderPage } from './pages/ModulePlaceholderPage'
import { ModulePickerPage } from './pages/ModulePickerPage'
import { UserPreferencesPage } from './pages/UserPreferencesPage'
import { VersionHistoryPage } from './pages/VersionHistoryPage'
import { StorageService } from '@/services/core/storage'
import './styles/index.css'

function MasqueradeGate() {
  const { search } = useLocation()
  StorageService.enableMasquerade()
  // Keeps the prefill a masquerade link from the users list carries.
  return <Navigate to={`/login${search}`} replace />
}

function RootRedirect() {
  if (AuthService.isAuthenticated()) {
    return <Navigate to="/select-module" replace />
  }
  return <Navigate to="/login" replace />
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  if (!AuthService.isAuthenticated()) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`
    return <Navigate to="/login" replace state={{ returnTo }} />
  }
  return <>{children}</>
}

function AppLayout() {
  return (
    <UserProvider>
      <UserProfileProvider>
        <NetworkProvider>
          <NetworkProfileProvider>
            <ModuleProvider>
              <NotificationsProvider>
                <CommandPalette />
                <Outlet />
              </NotificationsProvider>
            </ModuleProvider>
          </NetworkProfileProvider>
        </NetworkProvider>
      </UserProfileProvider>
    </UserProvider>
  )
}

function Protected({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute>{children}</ProtectedRoute>
}

const router = createBrowserRouter([
  // Framed by the creative preview window; it needs none of the app's providers
  { path: '/preview-creative/:creativeId/frame', element: <CreativePreviewRenderPage /> },
  {
    element: <AppLayout />,
    errorElement: <ErrorPage />,
    children: [
      { path: '/', element: <RootRedirect /> },
      { path: '/login', element: <LoginPage /> },
      { path: '/masquerade', element: <MasqueradeGate /> },
      { path: '/campaign-info/:campaignId', element: <PublicCampaignInfoPage /> },
      { path: '/political-preview', element: <PoliticalPreviewPage /> },
      { path: '/preview-creative/:creativeId', element: <CreativeBlankPreviewPage /> },
      { path: '/ai', element: <AiConnectPage /> },
      { path: '/select-module', element: <Protected><ModulePickerPage /></Protected> },
      { path: '/preferences', element: <Protected><UserPreferencesPage /></Protected> },
      { path: '/version-history', element: <Protected><VersionHistoryPage /></Protected> },

      // Political module
      { path: '/political', element: <Protected><DashboardPage /></Protected> },
      { path: '/political/elections', element: <Protected><ElectionsPage /></Protected> },
      { path: '/political/elections/:id', element: <Protected><ElectionDetailPage /></Protected> },
      { path: '/political/entities', element: <Protected><PoliticalEntitiesPage /></Protected> },
      { path: '/political/entities/:id', element: <Protected><PoliticalEntityEditPage /></Protected> },
      { path: '/political/campaigns', element: <Protected><PoliticalCampaignsPage /></Protected> },
      { path: '/political/campaigns/:id', element: <Protected><PoliticalCampaignEditPage /></Protected> },
      { path: '/political/users', element: <Protected><UsersPage basePath="/political/users" /></Protected> },
      { path: '/political/users/:id', element: <Protected><UserEditPage basePath="/political/users" /></Protected> },

      // Ad serving module
      { path: '/advertising', element: <Protected><AdvertisingHomePage /></Protected> },
      { path: '/advertising/advertisers', element: <Protected><AdvertisersPage /></Protected> },
      { path: '/advertising/advertisers/:id', element: <Protected><AdvertiserEditPage /></Protected> },
      { path: '/advertising/orders', element: <Protected><OrdersPage /></Protected> },
      { path: '/advertising/orders/:id', element: <Protected><OrderEditPage /></Protected> },
      { path: '/advertising/lineitems', element: <Protected><LineItemsPage /></Protected> },
      { path: '/advertising/lineitems/:id', element: <Protected><LineItemEditPage /></Protected> },
      { path: '/advertising/creatives', element: <Protected><CreativesPage /></Protected> },
      { path: '/advertising/creatives/upload', element: <Protected><CreativeBulkUploadPage /></Protected> },
      { path: '/advertising/creatives/:id', element: <Protected><CreativeEditPage /></Protected> },
      { path: '/advertising/reports', element: <Protected><QuickReportPage mode="advertising" /></Protected> },
      { path: '/advertising/explore', element: <Protected><ExplorePage /></Protected> },
      { path: '/advertising/*', element: <Protected><ModulePlaceholderPage /></Protected> },

      // Publishing module
      { path: '/publishing', element: <Protected><PublishingHomePage /></Protected> },
      { path: '/publishing/earnings', element: <Protected><EarningsAccountsPage /></Protected> },
      { path: '/publishing/earnings/:id', element: <Protected><EarningsAccountEditPage /></Protected> },
      { path: '/publishing/sites', element: <Protected><SitesPage /></Protected> },
      { path: '/publishing/sites/:id', element: <Protected><SiteEditPage /></Protected> },
      { path: '/publishing/site-groups', element: <Protected><SiteGroupsPage /></Protected> },
      { path: '/publishing/site-groups/:id', element: <Protected><SiteGroupEditPage /></Protected> },
      { path: '/publishing/ad-units', element: <Protected><AdUnitsPage /></Protected> },
      { path: '/publishing/ad-units/:id', element: <Protected><AdUnitEditPage /></Protected> },
      { path: '/publishing/blocklists', element: <Protected><BlocklistsPage /></Protected> },
      { path: '/publishing/blocklists/:id', element: <Protected><BlocklistEditPage /></Protected> },
      { path: '/publishing/rulesets', element: <Protected><RulesetsPage /></Protected> },
      { path: '/publishing/rulesets/:id', element: <Protected><RulesetEditPage /></Protected> },
      { path: '/publishing/reports', element: <Protected><QuickReportPage mode="publishing" /></Protected> },
      { path: '/publishing/*', element: <Protected><ModulePlaceholderPage /></Protected> },

      // Reports
      { path: '/reports', element: <Protected><ReportsPage /></Protected> },
      { path: '/reports/templates', element: <Protected><ReportTemplatesPage /></Protected> },
      { path: '/reports/templates/:id', element: <Protected><ReportTemplateEditPage /></Protected> },
      { path: '/reports/schedules', element: <Protected><ReportSchedulesPage /></Protected> },
      { path: '/reports/schedules/:id', element: <Protected><ReportScheduleEditPage /></Protected> },
      { path: '/reports/translations', element: <Protected><ReportTranslationsPage /></Protected> },
      { path: '/reports/translations/:id', element: <Protected><ReportTranslationEditPage /></Protected> },
      { path: '/reports/*', element: <Protected><ModulePlaceholderPage /></Protected> },

      // Admin module
      { path: '/admin/layouts', element: <Protected><LayoutsPage /></Protected> },
      { path: '/admin/layouts/:id', element: <Protected><LayoutEditPage /></Protected> },
      { path: '/admin/users', element: <Protected><UsersPage basePath="/admin/users" /></Protected> },
      { path: '/admin/users/:id', element: <Protected><AdminUserEditPage /></Protected> },
      { path: '/admin/networks', element: <Protected><NetworkEditPage /></Protected> },
      { path: '/admin/products', element: <Protected><ProductsPage /></Protected> },
      { path: '/admin/products/:id', element: <Protected><ProductEditPage /></Protected> },
      { path: '/admin/teams', element: <Protected><TeamsPage /></Protected> },
      { path: '/admin/teams/:id', element: <Protected><TeamEditPage /></Protected> },
      { path: '/admin/team-groups', element: <Protected><TeamGroupsPage /></Protected> },
      { path: '/admin/team-groups/:id', element: <Protected><TeamGroupEditPage /></Protected> },
      { path: '/admin/roles', element: <Protected><RolesPage /></Protected> },
      { path: '/admin/roles/:id', element: <Protected><RoleEditPage /></Protected> },
      { path: '/admin/custom-events', element: <Protected><CustomEventsPage /></Protected> },
      { path: '/admin/custom-events/:id', element: <Protected><CustomEventEditPage /></Protected> },
      { path: '/admin/tiers', element: <Protected><TiersPage /></Protected> },
      { path: '/admin/tiers/:id', element: <Protected><TierEditPage /></Protected> },
      { path: '/admin/data-exports', element: <Protected><DataExportsPage /></Protected> },
      { path: '/admin/data-exports/:id', element: <Protected><DataExportEditPage /></Protected> },
      { path: '/admin/email-templates', element: <Protected><EmailTemplatesPage /></Protected> },
      { path: '/admin/email-templates/:id', element: <Protected><EmailTemplateEditPage /></Protected> },
      { path: '/admin/email-translations', element: <Protected><EmailTranslationsPage /></Protected> },
      { path: '/admin/email-translations/:id', element: <Protected><EmailTranslationEditPage /></Protected> },
      { path: '/admin/notification-preferences', element: <Protected><NotificationPreferencesPage /></Protected> },
      { path: '/admin', element: <Protected><ModulePlaceholderPage /></Protected> },
      { path: '/admin/*', element: <Protected><ModulePlaceholderPage /></Protected> },

      // Super admin module
      { path: '/super-admin/release-notes', element: <Protected><ReleaseNotesPage /></Protected> },
      { path: '/super-admin/release-notes/:id', element: <Protected><ReleaseNoteEditPage /></Protected> },
      { path: '/super-admin/diagnostics', element: <Protected><DiagnosticsPage /></Protected> },
      { path: '/super-admin', element: <Protected><ModulePlaceholderPage /></Protected> },
      { path: '/super-admin/*', element: <Protected><ModulePlaceholderPage /></Protected> },

      { path: '*', element: <Navigate to="/login" replace /> },
    ],
  },
])

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider delayDuration={300}>
          <Toaster position="top-center" />
          <RouterProvider router={router} />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export default App
